import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryStates, parseAsStringLiteral } from 'nuqs';
import {
  Folder as FolderIcon,
  Film,
  AlertTriangle,
  Download,
  Trash2,
  X,
} from 'lucide-react';
import {
  Button,
  Checkbox,
  IconButton,
  Skeleton,
  Spinner,
  Table,
  Tooltip,
  type TableSortDirection,
} from '@ntconsult/aluma-react';
import { cn } from '@common/utils/cn';
import {
  formatDateTime,
  formatFileSize,
  formatRelativeTime,
} from '@common/utils/format';
import { ResourceActionMenu } from '@common/components/ResourceActionMenu';
import { SharedBadge } from '@common/components/SharedBadge';
import { useIsMobile } from '@common/hooks/useMediaQuery';
import type { File } from '../types';
import type { Folder } from '@features/folders';

interface BulkDeletePayload {
  folders: Folder[];
  files: File[];
}

interface UnifiedExplorerListProps {
  folders: Folder[];
  files: File[];
  /**
   * Identificador de contexto da lista (ex.: folderId, "root", "shared").
   * Quando muda, a seleção é resetada — evita carregar IDs de outra pasta.
   */
  scope: string;
  folderCanWrite?: (folder: Folder) => boolean;
  fileCanWrite?: (file: File) => boolean;
  onOpenFile?: (file: File) => void;
  onEditFile?: (file: File) => void;
  onDeleteFile?: (file: File) => void;
  onDownloadFile?: (file: File) => void;
  onShareFile?: (file: File) => void;
  onEditFolder?: (folder: Folder) => void;
  onDeleteFolder?: (folder: Folder) => void;
  onShareFolder?: (folder: Folder) => void;
  onDownloadFolder?: (folder: Folder) => void;
  /**
   * Quando definido, habilita seleção múltipla + ação "Baixar".
   * Recebe arquivos E pastas selecionados — backend gera ZIP por pasta e um
   * único ZIP agregando os arquivos soltos.
   */
  onBulkDownload?: (payload: { files: File[]; folders: Folder[] }) => void;
  /** Quando definido, habilita seleção múltipla + ação "Excluir". */
  onBulkDelete?: (payload: BulkDeletePayload) => void;
}

type Row =
  | { kind: 'folder'; folder: Folder }
  | { kind: 'file'; file: File };

type SortKey = 'name' | 'date' | 'size';
type SortDir = 'asc' | 'desc';
interface SortState {
  key: SortKey;
  dir: SortDir;
}

const SORT_KEYS = ['name', 'date', 'size'] as const satisfies readonly SortKey[];
const SORT_DIRS = ['asc', 'desc'] as const satisfies readonly SortDir[];

const sortParsers = {
  sortBy: parseAsStringLiteral(SORT_KEYS).withDefault('name'),
  sortDir: parseAsStringLiteral(SORT_DIRS).withDefault('asc'),
};

function timestamp(date: string | Date | undefined): number {
  return date ? new Date(date).getTime() : 0;
}

function compareFolders(a: Folder, b: Folder, sort: SortState): number {
  const sign = sort.dir === 'asc' ? 1 : -1;
  switch (sort.key) {
    case 'name':
      return a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }) * sign;
    case 'date':
      return (timestamp(a.updatedAt || a.createdAt) - timestamp(b.updatedAt || b.createdAt)) * sign;
    case 'size':
      // Pastas não têm tamanho — ordena por nome dentro do grupo.
      return a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' });
  }
}

function compareFiles(a: File, b: File, sort: SortState): number {
  const sign = sort.dir === 'asc' ? 1 : -1;
  switch (sort.key) {
    case 'name':
      return a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }) * sign;
    case 'date':
      return (timestamp(a.updatedAt || a.createdAt) - timestamp(b.updatedAt || b.createdAt)) * sign;
    case 'size':
      return ((a.size ?? 0) - (b.size ?? 0)) * sign;
  }
}

function nextDirection(current: TableSortDirection): SortDir {
  return current === 'asc' ? 'desc' : 'asc';
}

function toggleInSet<T>(set: Set<T>, value: T): Set<T> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

export function UnifiedExplorerList({
  folders,
  files,
  scope,
  folderCanWrite,
  fileCanWrite,
  onOpenFile,
  onEditFile,
  onDeleteFile,
  onDownloadFile,
  onShareFile,
  onEditFolder,
  onDeleteFolder,
  onShareFolder,
  onDownloadFolder,
  onBulkDownload,
  onBulkDelete,
}: UnifiedExplorerListProps) {
  const { t } = useTranslation(['files', 'common', 'folders']);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [{ sortBy, sortDir }, setSortParams] = useQueryStates(sortParsers, {
    history: 'replace',
  });
  const sort = useMemo<SortState>(() => ({ key: sortBy, dir: sortDir }), [sortBy, sortDir]);
  const [selectedFolderIds, setSelectedFolderIds] = useState<Set<string>>(new Set());
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());
  const lastClickedIndexRef = useRef<number | null>(null);
  const shiftRef = useRef(false);

  const selectionEnabled = !!(onBulkDownload || onBulkDelete);

  // Reset seleção ao trocar de contexto (navegar entre pastas, alternar para "compartilhado", etc.).
  useEffect(() => {
    setSelectedFolderIds(new Set());
    setSelectedFileIds(new Set());
    lastClickedIndexRef.current = null;
  }, [scope]);

  // Poda IDs órfãos quando os arrays externos mudam (ex.: após bulk delete +
  // refetch, IDs deletados ainda estariam no Set). O early-return preserva a
  // identidade do Set quando nada muda, evitando renders desnecessários.
  useEffect(() => {
    const ids = new Set(folders.map((f) => f.id));
    setSelectedFolderIds((prev) => {
      const next = new Set([...prev].filter((id) => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [folders]);

  useEffect(() => {
    const ids = new Set(files.map((f) => f.id));
    setSelectedFileIds((prev) => {
      const next = new Set([...prev].filter((id) => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [files]);

  const rows = useMemo<Row[]>(() => {
    const sortedFolders = [...folders].sort((a, b) => compareFolders(a, b, sort));
    const sortedFiles = [...files].sort((a, b) => compareFiles(a, b, sort));
    return [
      ...sortedFolders.map<Row>((folder) => ({ kind: 'folder', folder })),
      ...sortedFiles.map<Row>((file) => ({ kind: 'file', file })),
    ];
  }, [folders, files, sort]);

  const selectedCount = selectedFolderIds.size + selectedFileIds.size;
  const allSelected = rows.length > 0 && selectedCount === rows.length;
  const someSelected = selectedCount > 0 && !allSelected;

  function applyRange(start: number, end: number, targetState: boolean) {
    const folderUpdates = new Set(selectedFolderIds);
    const fileUpdates = new Set(selectedFileIds);
    for (let i = start; i <= end; i++) {
      const r = rows[i];
      if (r.kind === 'folder') {
        if (targetState) folderUpdates.add(r.folder.id);
        else folderUpdates.delete(r.folder.id);
      } else {
        if (targetState) fileUpdates.add(r.file.id);
        else fileUpdates.delete(r.file.id);
      }
    }
    setSelectedFolderIds(folderUpdates);
    setSelectedFileIds(fileUpdates);
  }

  function handleRowSelect(index: number, checked: boolean) {
    const row = rows[index];
    const lastIndex = lastClickedIndexRef.current;

    if (shiftRef.current && lastIndex !== null && lastIndex !== index) {
      const start = Math.min(lastIndex, index);
      const end = Math.max(lastIndex, index);
      applyRange(start, end, checked);
    } else if (row.kind === 'folder') {
      setSelectedFolderIds((prev) => toggleInSet(prev, row.folder.id));
    } else {
      setSelectedFileIds((prev) => toggleInSet(prev, row.file.id));
    }
    lastClickedIndexRef.current = index;
  }

  function handleToggleAll(checked: boolean) {
    if (checked) {
      setSelectedFolderIds(new Set(folders.map((f) => f.id)));
      setSelectedFileIds(new Set(files.map((f) => f.id)));
    } else {
      setSelectedFolderIds(new Set());
      setSelectedFileIds(new Set());
    }
    lastClickedIndexRef.current = null;
  }

  function clearSelection() {
    setSelectedFolderIds(new Set());
    setSelectedFileIds(new Set());
    lastClickedIndexRef.current = null;
  }

  function getSelectedFolders(): Folder[] {
    return folders.filter((f) => selectedFolderIds.has(f.id));
  }
  function getSelectedFiles(): File[] {
    return files.filter((f) => selectedFileIds.has(f.id));
  }

  const dirFor = (key: SortKey): TableSortDirection =>
    sort.key === key ? sort.dir : 'none';
  const toggleSort = (key: SortKey) =>
    setSortParams({
      sortBy: key,
      sortDir: sort.key === key ? nextDirection(sort.dir) : 'asc',
    });

  if (isMobile) {
    return (
      <div className="space-y-3">
        {selectionEnabled && selectedCount > 0 && (
          <BulkActionsBar
            selectedCount={selectedCount}
            canDownload={!!onBulkDownload}
            canDelete={!!onBulkDelete}
            onDownload={() =>
              onBulkDownload?.({
                files: getSelectedFiles(),
                folders: getSelectedFolders(),
              })
            }
            onDelete={() =>
              onBulkDelete?.({ folders: getSelectedFolders(), files: getSelectedFiles() })
            }
            onClear={clearSelection}
            sticky
          />
        )}

        <ul
          role="list"
          aria-label={t('files:table.name')}
          className="bg-surface rounded-lg border border-border divide-y divide-border overflow-hidden"
        >
          {rows.map((row, index) =>
            row.kind === 'folder' ? (
              <MobileFolderItem
                key={`folder-${row.folder.id}`}
                folder={row.folder}
                canWrite={folderCanWrite ? folderCanWrite(row.folder) : true}
                onOpen={() => navigate(`/files/${row.folder.id}`)}
                onEdit={onEditFolder}
                onDelete={onDeleteFolder}
                onShare={onShareFolder}
                onDownload={onDownloadFolder}
                selectionEnabled={selectionEnabled}
                selected={selectedFolderIds.has(row.folder.id)}
                onSelectChange={(checked) => handleRowSelect(index, checked)}
              />
            ) : (
              <MobileFileItem
                key={`file-${row.file.id}`}
                file={row.file}
                canWrite={fileCanWrite ? fileCanWrite(row.file) : true}
                onOpen={onOpenFile}
                onEdit={onEditFile}
                onDelete={onDeleteFile}
                onDownload={onDownloadFile}
                onShare={onShareFile}
                selectionEnabled={selectionEnabled}
                selected={selectedFileIds.has(row.file.id)}
                onSelectChange={(checked) => handleRowSelect(index, checked)}
              />
            ),
          )}
        </ul>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {selectionEnabled && selectedCount > 0 && (
        <BulkActionsBar
          selectedCount={selectedCount}
          canDownload={!!onBulkDownload}
          canDelete={!!onBulkDelete}
          onDownload={() =>
            onBulkDownload?.({
              files: getSelectedFiles(),
              folders: getSelectedFolders(),
            })
          }
          onDelete={() =>
            onBulkDelete?.({ folders: getSelectedFolders(), files: getSelectedFiles() })
          }
          onClear={clearSelection}
        />
      )}

      <Table containerClassName="bg-surface rounded-lg border border-border">
        <Table.Head>
          <Table.Row>
            {selectionEnabled && (
              <Table.Header className="w-12">
                <div
                  className="flex items-center"
                  onMouseDown={(e) => {
                    shiftRef.current = e.shiftKey;
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onCheckedChange={handleToggleAll}
                    label={
                      <span className="sr-only">
                        {allSelected || someSelected
                          ? t('common:bulkSelection.clear')
                          : t('common:bulkSelection.selectAll')}
                      </span>
                    }
                  />
                </div>
              </Table.Header>
            )}
            <Table.Header className="w-12"></Table.Header>
            <Table.Header
              sortable
              sortDirection={dirFor('name')}
              onSortChange={() => toggleSort('name')}
            >
              {t('files:table.name')}
            </Table.Header>
            <Table.Header
              className="w-40"
              sortable
              sortDirection={dirFor('date')}
              onSortChange={() => toggleSort('date')}
            >
              {t('files:table.date')}
            </Table.Header>
            <Table.Header
              className="w-28"
              sortable
              sortDirection={dirFor('size')}
              onSortChange={() => toggleSort('size')}
            >
              {t('files:table.size')}
            </Table.Header>
            <Table.Header className="w-16"></Table.Header>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {rows.map((row, index) =>
            row.kind === 'folder' ? (
              <FolderRow
                key={`folder-${row.folder.id}`}
                folder={row.folder}
                canWrite={folderCanWrite ? folderCanWrite(row.folder) : true}
                onOpen={() => navigate(`/files/${row.folder.id}`)}
                onEdit={onEditFolder}
                onDelete={onDeleteFolder}
                onShare={onShareFolder}
                onDownload={onDownloadFolder}
                selectionEnabled={selectionEnabled}
                selected={selectedFolderIds.has(row.folder.id)}
                onSelectChange={(checked) => handleRowSelect(index, checked)}
                onSelectMouseDown={(e) => {
                  shiftRef.current = e.shiftKey;
                }}
              />
            ) : (
              <FileRow
                key={`file-${row.file.id}`}
                file={row.file}
                canWrite={fileCanWrite ? fileCanWrite(row.file) : true}
                onOpen={onOpenFile}
                onEdit={onEditFile}
                onDelete={onDeleteFile}
                onDownload={onDownloadFile}
                onShare={onShareFile}
                selectionEnabled={selectionEnabled}
                selected={selectedFileIds.has(row.file.id)}
                onSelectChange={(checked) => handleRowSelect(index, checked)}
                onSelectMouseDown={(e) => {
                  shiftRef.current = e.shiftKey;
                }}
              />
            ),
          )}
        </Table.Body>
      </Table>
    </div>
  );
}

interface BulkActionsBarProps {
  selectedCount: number;
  canDownload: boolean;
  canDelete: boolean;
  onDownload: () => void;
  onDelete: () => void;
  onClear: () => void;
  sticky?: boolean;
}

function BulkActionsBar({
  selectedCount,
  canDownload,
  canDelete,
  onDownload,
  onDelete,
  onClear,
  sticky = false,
}: BulkActionsBarProps) {
  const { t } = useTranslation('common');

  return (
    <div
      role="region"
      aria-label={t('bulkSelection.selected', { count: selectedCount })}
      className={cn(
        'flex items-center justify-between gap-3 rounded-lg border border-border bg-surface-raised px-3 py-2',
        sticky && 'sticky top-2 z-10 shadow-sm',
      )}
    >
      <div className="flex items-center gap-2">
        <IconButton
          variant="ghost"
          size="sm"
          onClick={onClear}
          label={t('bulkSelection.clear')}
          icon={<X className="w-4 h-4" aria-hidden="true" />}
        />
        <span className="text-sm font-medium text-foreground">
          {t('bulkSelection.selected', { count: selectedCount })}
        </span>
      </div>

      <div className="flex items-center gap-2">
        {canDownload && (
          <Button variant="secondary" size="sm" onClick={onDownload}>
            <Download className="w-4 h-4" aria-hidden="true" />
            {t('actions.download')}
          </Button>
        )}
        {canDelete && (
          <Button variant="danger" size="sm" onClick={onDelete}>
            <Trash2 className="w-4 h-4" aria-hidden="true" />
            {t('actions.delete')}
          </Button>
        )}
      </div>
    </div>
  );
}

interface SelectionRowProps {
  selectionEnabled: boolean;
  selected: boolean;
  onSelectChange: (checked: boolean) => void;
  onSelectMouseDown: (e: React.MouseEvent) => void;
}

function RowSelectCell({
  selected,
  onSelectChange,
  onSelectMouseDown,
  ariaLabel,
}: Omit<SelectionRowProps, 'selectionEnabled'> & { ariaLabel: string }) {
  return (
    <Table.Cell>
      <div
        className="flex items-center"
        onMouseDown={onSelectMouseDown}
        onClick={(e) => e.stopPropagation()}
      >
        <Checkbox
          checked={selected}
          onCheckedChange={onSelectChange}
          label={<span className="sr-only">{ariaLabel}</span>}
        />
      </div>
    </Table.Cell>
  );
}

interface FolderRowProps extends SelectionRowProps {
  folder: Folder;
  canWrite: boolean;
  onOpen: () => void;
  onEdit?: (folder: Folder) => void;
  onDelete?: (folder: Folder) => void;
  onShare?: (folder: Folder) => void;
  onDownload?: (folder: Folder) => void;
}

function FolderRow({
  folder,
  canWrite,
  onOpen,
  onEdit,
  onDelete,
  onShare,
  onDownload,
  selectionEnabled,
  selected,
  onSelectChange,
  onSelectMouseDown,
}: FolderRowProps) {
  const { t } = useTranslation('common');
  const date = folder.updatedAt || folder.createdAt;

  return (
    <Table.Row
      interactive
      selected={selected}
      onClick={onOpen}
      aria-label={folder.name}
      className="group"
    >
      {selectionEnabled && (
        <RowSelectCell
          selected={selected}
          onSelectChange={onSelectChange}
          onSelectMouseDown={onSelectMouseDown}
          ariaLabel={t('bulkSelection.selectRow', { name: folder.name })}
        />
      )}
      <Table.Cell>
        <FolderIcon
          className="w-5 h-5 text-amber-400"
          fill="currentColor"
          strokeWidth={1.5}
          aria-hidden="true"
        />
      </Table.Cell>
      <Table.Cell>
        <div className="flex items-center gap-2">
          <span className="font-medium text-foreground group-hover:text-brand-primary transition-colors">
            {folder.name}
          </span>
          {folder.isShared && <SharedBadge />}
        </div>
      </Table.Cell>
      <Table.Cell className="text-foreground-muted">
        <DateCell date={date} />
      </Table.Cell>
      <Table.Cell className="text-foreground-subtle">—</Table.Cell>
      <Table.Cell>
        <ResourceActionMenu
          canWrite={canWrite}
          onDownload={onDownload ? () => onDownload(folder) : undefined}
          onShare={onShare ? () => onShare(folder) : undefined}
          onEdit={onEdit ? () => onEdit(folder) : undefined}
          onDelete={onDelete ? () => onDelete(folder) : undefined}
        />
      </Table.Cell>
    </Table.Row>
  );
}

interface FileRowProps extends SelectionRowProps {
  file: File;
  canWrite: boolean;
  onOpen?: (file: File) => void;
  onEdit?: (file: File) => void;
  onDelete?: (file: File) => void;
  onDownload?: (file: File) => void;
  onShare?: (file: File) => void;
}

function FileRow({
  file,
  canWrite,
  onOpen,
  onEdit,
  onDelete,
  onDownload,
  onShare,
  selectionEnabled,
  selected,
  onSelectChange,
  onSelectMouseDown,
}: FileRowProps) {
  const { t } = useTranslation(['files', 'common']);

  const isProcessing = file.status === 'processing' || file.status === 'confirmed';
  const isFailed = file.status === 'failed';
  const isInteractive = !isProcessing && !isFailed;
  const date = file.updatedAt || file.createdAt;

  const rowProps = isInteractive
    ? { interactive: true as const, onClick: () => onOpen?.(file), 'aria-label': file.name }
    : { 'aria-label': file.name };

  return (
    <Table.Row
      {...rowProps}
      selected={selected}
      className={cn('group', !isInteractive && 'opacity-80')}
    >
      {selectionEnabled && (
        <RowSelectCell
          selected={selected}
          onSelectChange={onSelectChange}
          onSelectMouseDown={onSelectMouseDown}
          ariaLabel={t('common:bulkSelection.selectRow', { name: file.name })}
        />
      )}
      <Table.Cell>
        {isProcessing ? (
          <Spinner size="sm" tone="brand" />
        ) : isFailed ? (
          <AlertTriangle className="w-5 h-5 text-danger" aria-hidden="true" />
        ) : (
          <Film className="w-5 h-5 text-info" aria-hidden="true" />
        )}
      </Table.Cell>
      <Table.Cell>
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'font-medium',
              isProcessing
                ? 'text-foreground-muted'
                : isFailed
                  ? 'text-danger'
                  : 'text-foreground group-hover:text-brand-primary transition-colors',
            )}
          >
            {file.name}
          </span>
          <span aria-live="polite">
            {isProcessing && (
              <span className="text-xs text-brand-primary">
                {t('files:processing', { defaultValue: 'Processando…' })}
              </span>
            )}
            {isFailed && (
              <span className="text-xs text-danger">
                {t('files:processingFailed', { defaultValue: 'Falha no processamento' })}
              </span>
            )}
          </span>
          {file.isShared && <SharedBadge />}
        </div>
      </Table.Cell>
      <Table.Cell className="text-foreground-muted">
        <DateCell date={date} />
      </Table.Cell>
      <Table.Cell className="text-foreground-muted">{formatFileSize(file.size)}</Table.Cell>
      <Table.Cell>
        <ResourceActionMenu
          canWrite={canWrite}
          isInteractive={isInteractive}
          onDownload={onDownload ? () => onDownload(file) : undefined}
          onShare={onShare ? () => onShare(file) : undefined}
          onEdit={onEdit ? () => onEdit(file) : undefined}
          onDelete={onDelete ? () => onDelete(file) : undefined}
        />
      </Table.Cell>
    </Table.Row>
  );
}

function DateCell({ date }: { date: string | Date }) {
  return (
    <Tooltip content={formatDateTime(date)}>
      <span>{formatRelativeTime(date)}</span>
    </Tooltip>
  );
}

interface MobileItemBaseProps {
  selectionEnabled: boolean;
  selected: boolean;
  onSelectChange: (checked: boolean) => void;
}

interface MobileFolderItemProps extends MobileItemBaseProps {
  folder: Folder;
  canWrite: boolean;
  onOpen: () => void;
  onEdit?: (folder: Folder) => void;
  onDelete?: (folder: Folder) => void;
  onShare?: (folder: Folder) => void;
  onDownload?: (folder: Folder) => void;
}

function MobileFolderItem({
  folder,
  canWrite,
  onOpen,
  onEdit,
  onDelete,
  onShare,
  onDownload,
  selectionEnabled,
  selected,
  onSelectChange,
}: MobileFolderItemProps) {
  const { t } = useTranslation('common');
  const date = folder.updatedAt || folder.createdAt;

  return (
    <li
      className={cn(
        'group flex items-center gap-3 px-3 py-3 min-h-14 active:bg-muted/60 transition-colors',
        selected && 'bg-muted/40',
      )}
    >
      {selectionEnabled && (
        <div
          className="shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={selected}
            onCheckedChange={onSelectChange}
            label={
              <span className="sr-only">
                {t('bulkSelection.selectRow', { name: folder.name })}
              </span>
            }
          />
        </div>
      )}
      <button
        type="button"
        onClick={onOpen}
        aria-label={folder.name}
        className="flex items-center gap-3 flex-1 min-w-0 text-left cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus rounded"
      >
        <FolderIcon
          className="w-6 h-6 text-amber-400 shrink-0"
          fill="currentColor"
          strokeWidth={1.5}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-medium text-foreground truncate">{folder.name}</span>
            {folder.isShared && <SharedBadge />}
          </div>
          <div className="text-xs text-foreground-muted">
            <DateCell date={date} />
          </div>
        </div>
      </button>
      <div className="shrink-0">
        <ResourceActionMenu
          canWrite={canWrite}
          onDownload={onDownload ? () => onDownload(folder) : undefined}
          onShare={onShare ? () => onShare(folder) : undefined}
          onEdit={onEdit ? () => onEdit(folder) : undefined}
          onDelete={onDelete ? () => onDelete(folder) : undefined}
        />
      </div>
    </li>
  );
}

interface MobileFileItemProps extends MobileItemBaseProps {
  file: File;
  canWrite: boolean;
  onOpen?: (file: File) => void;
  onEdit?: (file: File) => void;
  onDelete?: (file: File) => void;
  onDownload?: (file: File) => void;
  onShare?: (file: File) => void;
}

function MobileFileItem({
  file,
  canWrite,
  onOpen,
  onEdit,
  onDelete,
  onDownload,
  onShare,
  selectionEnabled,
  selected,
  onSelectChange,
}: MobileFileItemProps) {
  const { t } = useTranslation(['files', 'common']);
  const isProcessing = file.status === 'processing' || file.status === 'confirmed';
  const isFailed = file.status === 'failed';
  const isInteractive = !isProcessing && !isFailed;
  const date = file.updatedAt || file.createdAt;

  const handlePress = () => {
    if (isInteractive) onOpen?.(file);
  };

  return (
    <li
      className={cn(
        'group flex items-center gap-3 px-3 py-3 min-h-14 transition-colors',
        isInteractive && 'active:bg-muted/60',
        selected && 'bg-muted/40',
        !isInteractive && 'opacity-80',
      )}
    >
      {selectionEnabled && (
        <div
          className="shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={selected}
            onCheckedChange={onSelectChange}
            label={
              <span className="sr-only">
                {t('common:bulkSelection.selectRow', { name: file.name })}
              </span>
            }
          />
        </div>
      )}
      <button
        type="button"
        onClick={handlePress}
        disabled={!isInteractive}
        aria-label={file.name}
        className="flex items-center gap-3 flex-1 min-w-0 text-left cursor-pointer disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus rounded"
      >
        <span className="shrink-0">
          {isProcessing ? (
            <Spinner size="sm" tone="brand" />
          ) : isFailed ? (
            <AlertTriangle className="w-6 h-6 text-danger" aria-hidden="true" />
          ) : (
            <Film className="w-6 h-6 text-info" aria-hidden="true" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className={cn(
                'font-medium truncate',
                isProcessing
                  ? 'text-foreground-muted'
                  : isFailed
                    ? 'text-danger'
                    : 'text-foreground',
              )}
            >
              {file.name}
            </span>
            {file.isShared && <SharedBadge />}
          </div>
          <div className="text-xs text-foreground-muted flex items-center gap-2">
            <DateCell date={date} />
            <span aria-hidden="true">·</span>
            <span>{formatFileSize(file.size)}</span>
          </div>
          <span aria-live="polite">
            {isProcessing && (
              <span className="text-xs text-brand-primary">
                {t('files:processing', { defaultValue: 'Processando…' })}
              </span>
            )}
            {isFailed && (
              <span className="text-xs text-danger">
                {t('files:processingFailed', { defaultValue: 'Falha no processamento' })}
              </span>
            )}
          </span>
        </div>
      </button>
      <div className="shrink-0">
        <ResourceActionMenu
          canWrite={canWrite}
          isInteractive={isInteractive}
          onDownload={onDownload ? () => onDownload(file) : undefined}
          onShare={onShare ? () => onShare(file) : undefined}
          onEdit={onEdit ? () => onEdit(file) : undefined}
          onDelete={onDelete ? () => onDelete(file) : undefined}
        />
      </div>
    </li>
  );
}

export function ExplorerListSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div
      className="bg-surface rounded-lg border border-border overflow-hidden"
      aria-busy="true"
      aria-live="polite"
    >
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-4 px-4 py-3 border-b border-border last:border-b-0"
        >
          <Skeleton shape="rect" className="w-5 h-5 shrink-0" />
          <Skeleton shape="text" className="flex-1 max-w-[40%]" />
          <Skeleton shape="text" className="w-24" />
          <Skeleton shape="text" className="w-16" />
          <Skeleton shape="circle" className="w-8 h-8 shrink-0" />
        </div>
      ))}
    </div>
  );
}
